/**
 * @fileoverview Telegram Mini App 登录 Hook
 * @description 封装 Telegram WebApp initData 获取和后端登录的完整流程
 * MVC 架构：Controller 层 Hook，协调 TG SDK 与后端 API 调用
 * @module hooks/useTelegramLogin
 */
import { useState, useCallback } from 'react';
import { telegramLogin } from '../services/authService';
import { saveToken, API_OK } from '../services/api';
import { isTelegramMiniApp, getTelegramInitData, getTelegramStartParam, hapticFeedback } from '../lib/telegram';
import type { LoginResponseData } from '../services/authService';

// ============================================================
// 类型定义
// ============================================================

/** TG 登录状态 */
export type TelegramLoginStatus = 'idle' | 'logging_in' | 'success' | 'error';

/** useTelegramLogin 返回值 */
export interface UseTelegramLoginReturn {
  /** 当前登录状态 */
  status: TelegramLoginStatus;
  /** 错误信息 */
  error: string | null;
  /** 是否在 TG 环境中 */
  isTgEnvironment: boolean;
  /** 执行 TG 登录 */
  loginWithTelegram: () => Promise<LoginResponseData | null>;
}

// ============================================================
// Hook 实现
// ============================================================

/**
 * Telegram Mini App 登录 Hook
 * @description 完整流程：
 *   1. 检测是否在 Telegram Mini App 环境中
 *   2. 获取 Telegram WebApp.initData
 *   3. 解析 start_param 作为邀请码（可选）
 *   4. 调用后端 /api/v1/tonLogin 验证并登录
 *   5. 保存 JWT Token，返回用户数据
 */
export function useTelegramLogin(): UseTelegramLoginReturn {
  const [status, setStatus] = useState<TelegramLoginStatus>('idle');
  const [error, setError] = useState<string | null>(null);

  const isTgEnvironment = isTelegramMiniApp();

  const loginWithTelegram = useCallback(async (): Promise<LoginResponseData | null> => {
    setStatus('logging_in');
    setError(null);

    try {
      const initData = getTelegramInitData();
      if (!initData) {
        throw new Error('未检测到 Telegram 环境，请通过 Telegram Bot 打开本应用');
      }

      // 尝试从 start_param 解析邀请码
      const startParam = getTelegramStartParam();
      let refCode: string | undefined;
      let refType: string | undefined;
      if (startParam) {
        // start_param 格式：ref_<code> 或直接是邀请码
        if (startParam.startsWith('ref_')) {
          refCode = startParam.slice(4);
          refType = '1';
        } else {
          refCode = startParam;
          refType = '1';
        }
      }

      const resp = await telegramLogin({ initData, refCode, refType });

      if (resp.status !== API_OK || !resp.data) {
        const msg = resp.msg || '登录失败，请重试';
        throw new Error(msg);
      }

      const loginData = resp.data;

      // 保存 Token
      if (loginData.token) {
        saveToken(loginData.token);
      }

      hapticFeedback('success');
      setStatus('success');
      return loginData;
    } catch (err) {
      const msg = err instanceof Error ? err.message : '登录失败，请重试';
      setError(msg);
      setStatus('error');
      hapticFeedback('error');
      return null;
    }
  }, []);

  return {
    status,
    error,
    isTgEnvironment,
    loginWithTelegram,
  };
}
